import { Button, Card, Checkbox, Collapse, Empty, Flex, Form, Input, Select, Space, Spin, Tooltip } from "antd"
import { useWatch } from "antd/es/form/Form"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import { paypalLogoUrl } from "helpers/constant"
import { numberToCurrency } from "helpers/string"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import TipSection from "ui/TipSection"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import AddCardModal from "pagesV2/pro/Bookings/BookingForm/AddCardModal"
import stripeIcon from "assets/imagesV2/settings/stripe.svg"
import { getFormattedBookingDate } from "ui/Booking/Details/utils"
import { Avatar } from "ui/Avatar"
import { getMediaUrl } from "utils/media-upload"
import { getExperienceMainPhoto } from "pagesV2/pro/GuideProfile/GuideExperiences/utils/getExperienceMainPhoto"
import { grossUpCardFee, hasCreationBakedFees, normalizeCardFeeInfo, roundToCents } from "utils/bookingCalculations"
import {
  CARD_FEE_LABEL,
  ADJUSTMENT_FEE_SETTLED_OPTIONS,
  LINK_PROVIDERS,
  METHOD_FEE_LABELS,
  NO_QUOTED_FEE_OPTIONS,
  OTHERS_TYPES,
  PAYPAL_CARD_FEE,
  PAYMENT_TABS,
  PLATFORM_FEE_ON_COLLECTION_OPTIONS
} from "./constants"
import { useTakePaymentForm } from "./useTakePaymentForm"
import styles from "./index.module.css"

const TakePaymentForm = ({
  remainingBalance,
  bookingUUID,
  bookingId,
  onClose,
  bookingTitle,
  bookedOn,
  clientName,
  clientEmail,
  experienceAvatar,
  paymentOption,
  adjustmentOutstandingBase,
  adjustmentFeeRate,
  bookingStatus,
  balanceTaxesAndFees,
  balanceFeeItems,
  // Defaults to true so the legacy booking-details call site, which doesn't know the paid amount,
  // keeps charging the platform fee rather than silently dropping it.
  isFirstCollection = true
}) => {
  const [form] = Form.useForm()
  const [modalForm] = Form.useForm()
  const { t } = useTranslation()

  const {
    activeTab,
    setActiveTab,
    linkProvider,
    setLinkProvider,
    tipAmount,
    setTipAmount,
    isPartialPayment,
    setIsPartialPayment,
    isOfflineTab,
    selectedCardId,
    setSelectedCardId,
    isModalVisible,
    setIsModalVisible,
    isAddingCard,
    isSubmitting,
    isStripeConnected,
    isPaypalConnected,
    isStripeChargeable,
    stripeProblemMessage,
    paymentMethods,
    paymentMethodsLoading,
    clientId,
    visibleTabs,
    linkInfoText,
    amountLabel,
    partialLabel,
    totalCollectedLabel,
    submitLabel,
    handleSubmit,
    createPaymentMethod,
    feeSummaryData
  } = useTakePaymentForm({ remainingBalance, bookingUUID, bookingId, onClose, clientEmail, form, modalForm })

  // The two call sites supply different shapes: the booking-details page sends a single
  // experience_avatar, the reservation detail page sends trip.photos (an array). getMediaUrl only
  // understands a single media object, so an array silently rendered no photo at all.
  // For the array, reuse the app-wide "main photo" rule (position 0, else the first) rather than
  // assuming index 0. Videos are excluded — a video-only experience shows no image until the
  // backend can supply a thumbnail.
  const avatarUrl = Array.isArray(experienceAvatar)
    ? getExperienceMainPhoto({ photos: experienceAvatar.filter((media) => media?.file_type !== "video") })
    : getMediaUrl(experienceAvatar)

  const amountValue = useWatch("amount", form)

  // The processing fee never belongs in the balance: the backend bakes one in at booking / link
  // creation, and this screen quotes a fresh one on the amount actually being collected. Leaving
  // the baked one in would charge the guest a processing fee twice. It comes off on every tab.
  // The platform fee only comes off for cash-on-day bookings paid offline, which never quoted the
  // guest a DS fee; every other option quoted a fee-inclusive price the guest agreed to, so its
  // platform fee still stands when they pay cash.
  const excludedFeeLabels =
    isOfflineTab && NO_QUOTED_FEE_OPTIONS.includes(paymentOption) ? METHOD_FEE_LABELS : [CARD_FEE_LABEL]
  const feeItemLabel = (item) => item?.label ?? item?.title
  const feeItemAmount = (item) => Number(item?.amount ?? item?.price) || 0

  const methodFeesInBalance = Array.isArray(balanceFeeItems)
    ? roundToCents(
        balanceFeeItems
          .filter((item) => excludedFeeLabels.includes(feeItemLabel(item)))
          .reduce((total, item) => total + feeItemAmount(item), 0)
      )
    : 0

  // The per-fee lines behind the Taxes & Fees row, with whatever was excluded above left out so the
  // children always add up to the row they sit under.
  const balanceFeeLines = (Array.isArray(balanceFeeItems) ? balanceFeeItems : [])
    .filter((item) => !excludedFeeLabels.includes(feeItemLabel(item)) && feeItemAmount(item) > 0)
    .map((item) => ({ label: feeItemLabel(item), amount: feeItemAmount(item) }))

  const balanceDue = roundToCents(Math.max((Number(remainingBalance) || 0) - methodFeesInBalance, 0))
  // Unchecked, the guide collects the whole balance and there is no amount field to read.
  const collectedAmount = isPartialPayment ? Number(amountValue) || 0 : balanceDue

  // When there is no outstanding balance, the booking amount can't be paid — only a tip can be collected.
  const isZeroBalance = balanceDue <= 0

  // Fees were already captured during the initial booking flow for requested bookings,
  // so no additional platform or card processing fees should be charged when collecting.
  const isRequestedBooking = bookingStatus?.toLowerCase() === "booking request"

  // outstanding_base is the remaining price-increase amount that still owes a fee (0 when the
  // adjustment is fully settled / there was no increase).
  const outstandingBase = Number(adjustmentOutstandingBase) || 0
  const hasOutstandingAdjustment = outstandingBase > 0

  const isCardOrLink = activeTab === PAYMENT_TABS.CARD || activeTab === PAYMENT_TABS.LINK
  // The platform fee is only captured through Stripe (card tab, or link tab with the Stripe provider),
  // never PayPal / cash / other.
  const isStripeCardOrLink = activeTab === PAYMENT_TABS.CARD || (activeTab === PAYMENT_TABS.LINK && linkProvider === LINK_PROVIDERS.STRIPE)

  // The card processing fee is charged on whatever is being collected now, on every option — a
  // deposit only ever paid the fee on the deposit. Requested bookings are the one exception: their
  // fees were taken in full at booking time.
  const skipCardFee = isRequestedBooking
  // Platform fee (guest portion) is collected on full-payment options — full amount today, full
  // amount by link, and cash on the day. It's charged as amount_charged_to_guest (e.g. 4.5%) on the
  // amount being collected, so a partial collection is fee'd on that amount only.
  // Cash on the day is included by client decision (2026-09-10): a guest who arrives without cash
  // and pays by card or link pays the fees, exactly as a card booking would. Only an actual cash
  // collection is fee-free, which falls out of the card/link gates below.
  // Never applied to requested bookings since fees were already taken at booking time.
  const collectPlatformFee = !isRequestedBooking && PLATFORM_FEE_ON_COLLECTION_OPTIONS.includes(paymentOption)

  // Commission rate the server applies when collecting (payment_summary.adjustment_fee_rate). It is
  // per-booking and already 0 for options that collect no fee, so it is preferred over the
  // account-level guest rate — that one is only a fallback for payloads without it.
  const platformFeeConfig = feeSummaryData?.platform_fee
  const accountGuestRate =
    platformFeeConfig?.charged_to_guest && platformFeeConfig?.amount_charged_to_guest?.type === "percentage"
      ? Number(platformFeeConfig.amount_charged_to_guest.value) || 0
      : 0
  // Only a positive per-booking rate overrides the account rate. It arrives as 0 whenever there is
  // no price increase, and treating that 0 as authoritative would zero out the platform fee on
  // options that DO collect one at collection time (cash-on-day especially). Whether a fee applies
  // at all is decided by the option gates below, not by this rate.
  const guestPlatformRate = Number(adjustmentFeeRate) > 0 ? Number(adjustmentFeeRate) : accountGuestRate

  const cardFeeConfig = feeSummaryData?.card_processing_fee
  const cardFeePassthrough = cardFeeConfig?.passthrough_enabled && cardFeeConfig?.charged_to_guest

  const selectedCard = paymentMethods?.find((pm) => pm?.payment_method_id === selectedCardId)
  const { rate: cardRate, fixed: cardFixed } = normalizeCardFeeInfo(selectedCard?.card_fee_info, 0.029)

  // Taxes & fees already contained in the outstanding balance, shown as its own Summary line so the
  // balance reads as base + taxes, and used below to keep commission off the tax portion. Null when
  // the backend hasn't reported the split — the Summary then falls back to a single Remaining
  // Balance row rather than inventing a number.
  const taxesAndFeesDue =
    balanceTaxesAndFees != null ? roundToCents(Math.max(Number(balanceTaxesAndFees) - methodFeesInBalance, 0)) : null
  const balanceBase = taxesAndFeesDue != null ? roundToCents(balanceDue - taxesAndFeesDue) : balanceDue

  // Fees owed on a given amount. Called twice: once for the whole balance (the Summary block, which
  // always shows the full picture) and once for the amount actually being collected (the partial
  // block).
  const computeFees = (amount) => {
    const isFeeAlreadyInPrice = hasCreationBakedFees(paymentOption)

    // The amount that still owes fees at all. When the DS fee was already included in the booked
    // price (paid in full by link, or in full today), charging it again on collection would bill the
    // guest twice — so only a later price increase (outstanding_base, capped at what's actually being
    // collected) still owes fees. Other options fee the amount collected.
    const feeableAmount = isFeeAlreadyInPrice ? Math.min(outstandingBase, amount) : amount

    // Commission is charged on the fee-free price only — taxes and fees sitting inside the balance
    // are not commissionable, so a $225 balance made of $175 price + $50 tax is fee'd on the $175.
    // The WHOLE base is charged on the first collection, even a partial one (client decision,
    // 2026-09-10): DS takes its fee up front rather than a slice per instalment, so collecting $100
    // of a $225 balance still carries the full 6% of $175. Later collections add nothing — the fee
    // is already paid — which is what isFirstCollection gates.
    const platformFeeBase = isFeeAlreadyInPrice ? feeableAmount : (isFirstCollection ? balanceBase : 0)

    // Platform fee — guest portion, applies to card and link tabs.
    const platformFee =
      collectPlatformFee && isCardOrLink && guestPlatformRate > 0 && platformFeeBase > 0
        ? roundToCents((platformFeeBase * guestPlatformRate) / 100)
        : 0

    // Adjustment platform fee — the guest's portion of the platform fee owed on a past upward price
    // adjustment, on the deposit + balance-in-cash options. The server charges it only for card /
    // Stripe link; cash, PayPal and the offline methods add nothing. Capped at the un-fee'd part of
    // the increase (outstanding_base) and at what's actually being collected, which is equivalent to
    // the server's min(amount x rate%, adjustment_fee_outstanding).
    // Deposit + balance-later options are excluded: their fee was settled at adjust time, so a
    // collection adds nothing at all.
    const adjustmentFee =
      !isRequestedBooking &&
      !collectPlatformFee &&
      !ADJUSTMENT_FEE_SETTLED_OPTIONS.includes(paymentOption) &&
      isStripeCardOrLink &&
      hasOutstandingAdjustment &&
      guestPlatformRate > 0
        ? roundToCents((Math.min(outstandingBase, amount) * guestPlatformRate) / 100)
        : 0

    // Card processing fee — gross-up so the guide nets the base after the processor's cut, matching
    // the server's (S x r + f) / (1 - r). Rates are per provider: the selected card's own for the
    // card tab, Stripe 2.9% + $0.30 or PayPal 3.49% + $0.49 for a payment link.
    // Charged on the WHOLE amount hitting the card — `amount`, not `feeableAmount` — plus any fee
    // being added on top. The processor takes its cut on everything it moves, so it is irrelevant
    // whether the DS fee was baked in at booking: the baked-in processing fee has already been
    // stripped out of the balance above, and this replaces it with one on the real charge.
    const grossUpBase = amount + platformFee + adjustmentFee
    let cardProcessingFee = 0
    if (!skipCardFee && isCardOrLink && cardFeePassthrough && grossUpBase > 0) {
      if (activeTab === PAYMENT_TABS.CARD) {
        cardProcessingFee = grossUpCardFee(grossUpBase, { rate: Number(cardRate), fixed: Number(cardFixed) })
      } else if (linkProvider === LINK_PROVIDERS.PAYPAL) {
        cardProcessingFee = grossUpCardFee(grossUpBase, { rate: PAYPAL_CARD_FEE.rate, fixed: PAYPAL_CARD_FEE.fixed })
      } else {
        cardProcessingFee = grossUpCardFee(grossUpBase, { rate: (cardFeeConfig?.percentage ?? 2.9) / 100, fixed: 0.3 })
      }
    }

    return { platformFee, adjustmentFee, cardProcessingFee }
  }

  const balanceFees = computeFees(balanceDue)
  const collectedFees = computeFees(collectedAmount)

  const cardFeeLabel = t("client.payment.collect.card_fee")

  // Summary always describes the whole outstanding balance, regardless of any partial amount.
  const summaryTotal = balanceDue + tipAmount + balanceFees.platformFee + balanceFees.adjustmentFee
  const summaryPaymentTotal = summaryTotal + balanceFees.cardProcessingFee

  // Partial block: what is actually being charged now, and what stays owed afterwards.
  const totalCollected = collectedAmount + tipAmount + collectedFees.platformFee + collectedFees.adjustmentFee + collectedFees.cardProcessingFee
  const outstandingAfter = roundToCents(Math.max(balanceDue - collectedAmount, 0))

  // Informational: outstanding adjustment platform fee (guest rate on the price increase) that
  // cash/PayPal/other won't capture.
  const outstandingPlatformFee = roundToCents((outstandingBase * guestPlatformRate) / 100)
  const showCashAdjustmentHint =
    !collectPlatformFee && !isStripeCardOrLink && hasOutstandingAdjustment && outstandingPlatformFee > 0

  // One "label ......... value" line of the summary / partial blocks. An outstanding balance is
  // styled red via the CSS token; every other row keeps the modal's secondary text colour.
  const summaryRow = (label, value, { isBold = false, hasDivider = false, isOutstanding = false } = {}) => {
    const textProps = isOutstanding
      ? { className: styles.outstandingValue }
      : { color: isBold ? colors.textPrimary : colors.textSecondaryDark }
    return (
      <Flex justify="space-between" className={hasDivider ? styles.summaryDivider : ""}>
        <Text fw={isBold ? 700 : 400} {...textProps}>
          {label}
        </Text>
        <Text fw={isBold ? 700 : 400} {...textProps}>
          {value}
        </Text>
      </Flex>
    )
  }

  // Taxes & Fees expands to the per-fee lines the backend reports inside the balance, matching the
  // booking screens. Falls back to a plain row when the payload carries no itemisation.
  const taxesAndFeesLabel = t("client.payment.collect.taxes_and_fees")
  const taxesAndFeesRow =
    balanceFeeLines.length > 0 ? (
      <Collapse
        ghost
        className={styles.taxesFeesCollapse}
        items={[
          {
            key: "taxes-fees",
            styles: { header: { padding: 0 }, body: { padding: "4px 0 0 16px" } },
            label: summaryRow(taxesAndFeesLabel, numberToCurrency(taxesAndFeesDue)),
            children: (
              <Flex vertical gap={4}>
                {balanceFeeLines.map((line) => (
                  <Flex key={line.label} justify="space-between">
                    <Text color={colors.textSecondaryDark} size="sm">
                      {line.label}
                    </Text>
                    <Text color={colors.textSecondaryDark} size="sm">
                      {numberToCurrency(line.amount)}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )
          }
        ]}
      />
    ) : (
      summaryRow(taxesAndFeesLabel, numberToCurrency(taxesAndFeesDue))
    )

  const amountField = (
    <Form.Item
      label={isOfflineTab ? amountLabel : null}
      name="amount"
      className={styles.partialAmountInput}
      rules={[
        { required: true, message: t("client.payment.collect.amount_required") },
        {
          validator: (_, value) => {
            if (!value) return Promise.reject()
            const num = Number.parseFloat(value)
            if (Number.isNaN(num) || num <= 0) return Promise.reject(t("client.payment.collect.amount_invalid"))
            if (num > balanceDue) return Promise.reject(t("client.payment.amountToPayCannotExceedOutstandingBalance"))
            return Promise.resolve()
          }
        }
      ]}
    >
      <Input prefix="$" type="number" min="0.01" step="0.01" />
    </Form.Item>
  )

  return (
    <Spin spinning={isSubmitting} tip={t("client.payment.collect.processing")}>
      <Space direction="vertical" size={24} className="w-full">
        <div className={styles.formContent}>
          <Space direction="vertical" size={24} className="w-full">
            {(bookingTitle || clientName || clientEmail) && (
              <Flex gap={12} align="center" className={styles.bookingInfo}>
                {avatarUrl && <Avatar fullName={bookingTitle} src={avatarUrl} shape="square" size={64} />}
                <Flex vertical gap={4}>
                  {bookingTitle && (
                    <Text fw={500}>
                      {bookingTitle}
                      {bookedOn ? ` · ${getFormattedBookingDate(bookedOn)}` : ""}
                    </Text>
                  )}
                  {(clientName || clientEmail) && (
                    <Text color={colors.textSecondaryDark}>{[clientName, clientEmail].filter(Boolean).join(" · ")}</Text>
                  )}
                </Flex>
              </Flex>
            )}

            <div>
              <Text className={styles.sectionLabel}>{t("client.payment.collect.choose_payment_method")}</Text>
              <div className={styles.tabContainer}>
                {visibleTabs.map((tab) => (
                  <Tooltip key={tab.key} title={tab.disabled ? tab.tooltip : ""}>
                    <button
                      type="button"
                      className={`${activeTab === tab.key ? styles.tabButtonActive : styles.tabButton} ${tab.disabled ? styles.tabButtonDisabled : ""}`}
                      onClick={() => !tab.disabled && setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>

            <Flex gap={spacings.XS} className={styles.balancePill}>
              <Text color={colors.textSecondaryDark}>{t("client.payment.collect.remaining_balance_due")}</Text>
              <Text color={colors.textPrimary} fw={500}>
                {numberToCurrency(balanceDue)}
              </Text>
            </Flex>

            <Form form={form} layout="vertical" initialValues={{ amount: remainingBalance }}>
              <div className={styles.tabContent}>
                {activeTab === PAYMENT_TABS.CARD && (
                  <Spin spinning={paymentMethodsLoading}>
                    {paymentMethods?.length ? (
                      <>
                        <div className={styles.cardListContainer}>
                          <PaymentMethodList
                            active={selectedCardId}
                            setActive={setSelectedCardId}
                            paymentMethods={paymentMethods}
                            clientId={clientId}
                          />
                        </div>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          style={{ color: colors.sportsmanBrand, fontWeight: 500, fontSize: 16, marginTop: 8 }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          {t("booking.labels.add_card")}
                        </Button>
                      </>
                    ) : (
                      !isAddingCard && (
                        <Card>
                          <Empty
                            image={<NoCardImage />}
                            description={
                              <>
                                <p>{t("booking.labels.no_card_title")}</p>
                                <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                  {t("booking.labels.add_card")}
                                </Button>
                              </>
                            }
                          />
                        </Card>
                      )
                    )}
                  </Spin>
                )}

                {activeTab === PAYMENT_TABS.LINK && (
                  <Space direction="vertical" className="w-full">
                    <Flex gap={8}>
                      {isStripeConnected && (
                        <Tooltip title={!isStripeChargeable ? stripeProblemMessage : ""}>
                          <button
                            type="button"
                            className={`${linkProvider === LINK_PROVIDERS.STRIPE ? styles.providerButtonActive : styles.providerButton} ${
                              !isStripeChargeable ? styles.providerButtonDisabled : ""
                            }`}
                            onClick={() => isStripeChargeable && setLinkProvider(LINK_PROVIDERS.STRIPE)}
                          >
                            <img src={stripeIcon} alt="Stripe" height={16} /> Stripe
                          </button>
                        </Tooltip>
                      )}
                      {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
                        <button
                          type="button"
                          className={linkProvider === LINK_PROVIDERS.PAYPAL ? styles.providerButtonActive : styles.providerButton}
                          onClick={() => setLinkProvider(LINK_PROVIDERS.PAYPAL)}
                        >
                          <img src={paypalLogoUrl} alt="PayPal" height={16} /> PayPal
                        </button>
                      )}
                    </Flex>
                    <div className={styles.linkInfo}>
                      <Text color={colors.textSecondaryDark}>{linkInfoText}</Text>
                    </div>
                  </Space>
                )}

                {activeTab === PAYMENT_TABS.OTHERS && (
                  <Space direction="vertical" className="w-full">
                    <Form.Item name="othersType" label="Payment Type" rules={[{ required: true, message: "Please select a payment type" }]}>
                      <Select placeholder="Select payment type" options={OTHERS_TYPES} />
                    </Form.Item>
                    <Form.Item name="note" label="Reference / Note">
                      <Input.TextArea placeholder="Add a reference number or note (optional)" rows={3} />
                    </Form.Item>
                  </Space>
                )}
              </div>

              {showCashAdjustmentHint && (
                <div className={styles.infoCallout}>
                  <InfoCircleOutlined className={styles.infoCalloutIcon} />
                  <Text color={colors.textSecondaryDark}>
                    {t("client.payment.collect.adjustment_fee_hint", { amount: numberToCurrency(outstandingPlatformFee) })}
                  </Text>
                </div>
              )}

              {activeTab === PAYMENT_TABS.CARD && (
                <TipSection baseAmount={remainingBalance} onTipChange={setTipAmount} disablePresets={isZeroBalance} />
              )}

              <Flex vertical gap={4} className={styles.summarySection}>
                <Text className={styles.sectionLabel}>{t("client.payment.collect.summary")}</Text>
                {summaryRow(t("client.payment.collect.remaining_balance"), numberToCurrency(balanceBase))}
                {tipAmount > 0 && summaryRow(t("client.payment.collect.tip"), numberToCurrency(tipAmount))}
                {taxesAndFeesDue > 0 && taxesAndFeesRow}
                {balanceFees.platformFee > 0 &&
                  summaryRow(platformFeeConfig?.title ?? "Platform Fee", `+${numberToCurrency(balanceFees.platformFee)}`)}
                {balanceFees.adjustmentFee > 0 &&
                  summaryRow(platformFeeConfig?.title ?? "Platform Fee", `+${numberToCurrency(balanceFees.adjustmentFee)}`)}
                {summaryRow(t("client.payment.collect.total"), numberToCurrency(summaryTotal), { isBold: true })}
                {balanceFees.cardProcessingFee > 0 && (
                  <>
                    {summaryRow(cardFeeLabel, `+${numberToCurrency(balanceFees.cardProcessingFee)}`, { hasDivider: true })}
                    {summaryRow(t("client.payment.collect.payment_total"), numberToCurrency(summaryPaymentTotal), { isBold: true })}
                  </>
                )}
              </Flex>

              {!isZeroBalance && (
                <Flex vertical gap={spacings.S} className={styles.partialSection}>
                  <Checkbox checked={isPartialPayment} onChange={(event) => setIsPartialPayment(event.target.checked)}>
                    {partialLabel}
                  </Checkbox>

                  {isPartialPayment && !isOfflineTab && (
                    <Flex vertical gap={4}>
                      <Flex justify="space-between" align="center" gap={spacings.S}>
                        <Text color={colors.textSecondaryDark}>{amountLabel}</Text>
                        {amountField}
                      </Flex>
                      {collectedFees.platformFee > 0 &&
                        summaryRow(platformFeeConfig?.title ?? "Platform Fee", `+${numberToCurrency(collectedFees.platformFee)}`)}
                      {collectedFees.adjustmentFee > 0 &&
                        summaryRow(platformFeeConfig?.title ?? "Platform Fee", `+${numberToCurrency(collectedFees.adjustmentFee)}`)}
                      {collectedFees.cardProcessingFee > 0 &&
                        summaryRow(cardFeeLabel, `+${numberToCurrency(collectedFees.cardProcessingFee)}`)}
                      {summaryRow(totalCollectedLabel, numberToCurrency(totalCollected), { isBold: true, hasDivider: true })}
                      {summaryRow(t("client.payment.collect.outstanding_balance"), numberToCurrency(outstandingAfter), { isOutstanding: true })}
                    </Flex>
                  )}

                  {isPartialPayment && isOfflineTab && (
                    <div className={styles.cashPartialGrid}>
                      <Flex vertical gap={spacings.XS}>
                        {amountField}
                        {activeTab === PAYMENT_TABS.CASH && (
                          <div className={styles.infoCallout}>
                            <InfoCircleOutlined className={styles.infoCalloutIcon} />
                            <Text color={colors.textSecondaryDark}>{t("client.payment.collect.cash_info")}</Text>
                          </div>
                        )}
                      </Flex>
                      <Flex vertical gap={4}>
                        {summaryRow(totalCollectedLabel, numberToCurrency(totalCollected), { isBold: true })}
                        {summaryRow(t("client.payment.collect.outstanding_balance"), numberToCurrency(outstandingAfter), {
                          isOutstanding: true,
                          hasDivider: true
                        })}
                      </Flex>
                    </div>
                  )}
                </Flex>
              )}
            </Form>
          </Space>
        </div>

        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button
            type="primary"
            className="py-l px-xl"
            disabled={isZeroBalance && tipAmount <= 0}
            onClick={() => handleSubmit(collectedAmount)}
          >
            {submitLabel}
          </Button>
        </Flex>
      </Space>

      {isModalVisible && (
        <AddCardModal
          visible={isModalVisible}
          onCancel={() => {
            modalForm.resetFields()
            setIsModalVisible(false)
          }}
          onSubmit={createPaymentMethod}
          form={modalForm}
          loading={isAddingCard}
        />
      )}
    </Spin>
  )
}

TakePaymentForm.propTypes = {
  remainingBalance: PropTypes.number,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  bookingTitle: PropTypes.string,
  bookedOn: PropTypes.string,
  clientName: PropTypes.string,
  clientEmail: PropTypes.string,
  experienceAvatar: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  paymentOption: PropTypes.string,
  adjustmentOutstandingBase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  adjustmentFeeRate: PropTypes.number,
  bookingStatus: PropTypes.string,
  balanceTaxesAndFees: PropTypes.number,
  // outstanding_breakdown.items — the per-fee lines inside the balance
  balanceFeeItems: PropTypes.array,
  // False once any payment has been recorded, so the platform fee is only ever charged once
  isFirstCollection: PropTypes.bool
}

export default TakePaymentForm
